import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../utils/cn';
const OFFSET = 4;
export const DropdownMenu = ({ trigger, children, align = 'start', side = 'bottom', className, }) => {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const menuId = useRef(`dropdown-${Math.random().toString(36).substring(2, 9)}`);
    const updatePosition = useCallback(() => {
        if (!triggerRef.current || !menuRef.current)
            return;
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const menuRect = menuRef.current.getBoundingClientRect();
        let top = 0;
        let left = 0;
        // Vertical positioning
        if (side === 'bottom') {
            top = triggerRect.bottom + OFFSET;
            if (top + menuRect.height > window.innerHeight - 8) {
                top = triggerRect.top - menuRect.height - OFFSET;
            }
        }
        else {
            top = triggerRect.top - menuRect.height - OFFSET;
            if (top < 8) {
                top = triggerRect.bottom + OFFSET;
            }
        }
        // Horizontal positioning
        switch (align) {
            case 'start':
                left = triggerRect.left;
                break;
            case 'center':
                left = triggerRect.left + (triggerRect.width - menuRect.width) / 2;
                break;
            case 'end':
                left = triggerRect.right - menuRect.width;
                break;
        }
        // Clamp to viewport
        left = Math.max(8, Math.min(left, window.innerWidth - menuRect.width - 8));
        top = Math.max(8, top);
        setPosition({ top, left });
    }, [align, side]);
    // Toggle the menu
    const toggleMenu = useCallback(() => {
        setOpen((prev) => !prev);
    }, []);
    const closeMenu = useCallback(() => {
        setOpen(false);
    }, []);
    // Update position on open
    useEffect(() => {
        if (open) {
            requestAnimationFrame(updatePosition);
        }
    }, [open, updatePosition]);
    // Close on outside click
    useEffect(() => {
        if (!open)
            return;
        const handleClickOutside = (event) => {
            if (menuRef.current &&
                !menuRef.current.contains(event.target) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target)) {
                closeMenu();
            }
        };
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                closeMenu();
                triggerRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [open, closeMenu]);
    // Keyboard navigation within the menu
    const handleMenuKeyDown = useCallback((event) => {
        const menu = menuRef.current;
        if (!menu)
            return;
        const items = Array.from(menu.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])'));
        const currentIndex = items.indexOf(document.activeElement);
        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
                items[nextIndex]?.focus();
                break;
            }
            case 'ArrowUp': {
                event.preventDefault();
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
                items[prevIndex]?.focus();
                break;
            }
            case 'Home': {
                event.preventDefault();
                items[0]?.focus();
                break;
            }
            case 'End': {
                event.preventDefault();
                items[items.length - 1]?.focus();
                break;
            }
            case 'Tab': {
                closeMenu();
                break;
            }
        }
    }, [closeMenu]);
    // Focus the first item when opening
    useEffect(() => {
        if (open && menuRef.current) {
            requestAnimationFrame(() => {
                const firstItem = menuRef.current?.querySelector('[role="menuitem"]:not([aria-disabled="true"])');
                firstItem?.focus();
            });
        }
    }, [open]);
    // Clone trigger
    const triggerElement = React.cloneElement(trigger, {
        ref: (node) => {
            triggerRef.current = node;
            const triggerOrigRef = trigger.ref;
            if (typeof triggerOrigRef === 'function') {
                triggerOrigRef(node);
            }
            else if (triggerOrigRef && typeof triggerOrigRef === 'object') {
                triggerOrigRef.current = node;
            }
        },
        onClick: (e) => {
            toggleMenu();
            trigger.props.onClick?.(e);
        },
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId.current : undefined,
    });
    return (_jsxs(_Fragment, { children: [triggerElement, open &&
                createPortal(_jsx("div", { ref: menuRef, id: menuId.current, role: "menu", "aria-orientation": "vertical", onKeyDown: handleMenuKeyDown, className: cn('fixed z-dropdown min-w-[180px] rounded-lg border border-dark-200 dark:border-dark-700 bg-white dark:bg-dark-800 shadow-dropdown py-1', 'animate-scale-in origin-top-left', className), style: { top: position.top, left: position.left }, children: React.Children.map(children, (child) => {
                        if (React.isValidElement(child)) {
                            return React.cloneElement(child, {
                                onClose: closeMenu,
                            });
                        }
                        return child;
                    }) }), document.body)] }));
};
DropdownMenu.displayName = 'DropdownMenu';
export const DropdownMenuItem = ({ icon, disabled = false, danger = false, onClose, shortcut, children, className, onClick, ...props }) => {
    const handleClick = (e) => {
        if (disabled)
            return;
        onClick?.(e);
        onClose?.();
    };
    const handleKeyDown = (e) => {
        if (disabled)
            return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.(e);
            onClose?.();
        }
    };
    return (_jsxs("div", { role: "menuitem", tabIndex: disabled ? -1 : 0, "aria-disabled": disabled || undefined, className: cn('flex items-center gap-2 px-3 py-2 text-sm cursor-pointer select-none transition-colors outline-none', danger
            ? 'text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20 focus:bg-danger-50 dark:focus:bg-danger-900/20'
            : 'text-dark-700 dark:text-dark-200 hover:bg-dark-100 dark:hover:bg-dark-700 focus:bg-dark-100 dark:focus:bg-dark-700', disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent', className), onClick: handleClick, onKeyDown: handleKeyDown, ...props, children: [icon && (_jsx("span", { className: "inline-flex shrink-0 w-4 h-4", "aria-hidden": "true", children: icon })), _jsx("span", { className: "flex-1", children: children }), shortcut && (_jsx("span", { className: "text-xs text-dark-400 dark:text-dark-500 ml-auto pl-4", children: shortcut }))] }));
};
DropdownMenuItem.displayName = 'DropdownMenuItem';
export const DropdownMenuSeparator = ({ className, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
onClose, ...props }) => {
    return (_jsx("div", { role: "separator", className: cn('h-px my-1 bg-dark-200 dark:bg-dark-700', className), ...props }));
};
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';
export const DropdownMenuLabel = ({ children, className, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
onClose, ...props }) => {
    return (_jsx("div", { className: cn('px-3 py-1.5 text-xs font-semibold text-dark-500 dark:text-dark-400 uppercase tracking-wider', className), ...props, children: children }));
};
DropdownMenuLabel.displayName = 'DropdownMenuLabel';
//# sourceMappingURL=DropdownMenu.js.map